/**
 * Financial Calculations Configuration
 * South African property transfer costs and fees
 *
 * Transfer Duty: SARS 2025/26 brackets (effective 1 April 2025, confirmed unchanged 2026/27)
 * Attorney Fees: LSSA Conveyancing Fee Guidelines (effective 1 August 2025)
 * VAT: 15.5% (effective 1 May 2025)
 * Bank Initiation Fee: NCA-regulated cap
 * Deeds Office Fees: Fixed values for v1.0 (Gazette 54225 scale available for v2.0)
 *
 * Last Updated: May 2026 — aligned to Transfer_Cost_Calculator_TechSpec_v1.0
 */

// ─── Types ──────────────────────────────────────────────────────────────────

export type PurchaserType =
  | 'Natural_person'
  | 'Company'
  | 'Closed_Corporation'
  | 'Trust';

export interface CalculatorConfig {
  vatRate: number;        // e.g. 0.155 for 15.5%
  bankInitFee: number;    // Bank initiation fee incl. VAT (NCA cap)
  bondDeedsFee: number;   // Bond deeds office fee (fixed v1.0)
  transferDeedsFee: number; // Transfer deeds office fee (fixed v1.0)
  postFicaFee: number;    // Post/petties/FICA fee incl. VAT (per side)
}

export interface TransferCostResult {
  bondAttorneyFee: number;
  bankInitFee: number;
  bondDeedsFee: number;
  bondPostFica: number;
  bondTotal: number;
  transferAttorneyFee: number;
  transferDuty: number;
  transferDeedsFee: number;
  transferPostFica: number;
  transferTotal: number;
  grandTotal: number;
}

export const DEFAULT_CALCULATOR_CONFIG: CalculatorConfig = {
  vatRate: 0.155,
  bankInitFee: 6064,
  bondDeedsFee: 2767,
  transferDeedsFee: 3296,
  postFicaFee: 2496,
};

// ─── Legacy interfaces (preserved for other calculator tabs) ────────────────

export interface TransferDutyBracket {
  min: number;
  max: number | null;
  rate: number;
  baseAmount: number;
}

export interface TransferFeesBracket {
  min: number;
  max: number | null;
  rate: number;
  baseAmount: number;
}

export interface BondRegistrationBracket {
  min: number;
  max: number | null;
  rate: number;
  baseAmount: number;
}

export interface FinancialConfig {
  transferDuty: {
    brackets: TransferDutyBracket[];
    exemptionThreshold: number;
  };
  transferFees: {
    brackets: TransferFeesBracket[];
  };
  bondRegistration: {
    brackets: BondRegistrationBracket[];
  };
  deedsOffice: {
    minimumFee: number;
    rate: number;
  };
  disbursements: {
    estimated: number;
  };
}

export const DEFAULT_FINANCIAL_CONFIG: FinancialConfig = {
  transferDuty: {
    exemptionThreshold: 1210000,
    brackets: [
      { min: 0, max: 1210000, rate: 0, baseAmount: 0 },
      { min: 1210000, max: 1663800, rate: 0.03, baseAmount: 0 },
      { min: 1663800, max: 2329300, rate: 0.06, baseAmount: 13614 },
      { min: 2329300, max: 2994800, rate: 0.08, baseAmount: 53544 },
      { min: 2994800, max: 13310000, rate: 0.11, baseAmount: 106784 },
      { min: 13310000, max: null, rate: 0.13, baseAmount: 1241456 },
    ],
  },
  transferFees: {
    brackets: [
      { min: 0, max: 300000, rate: 0.03, baseAmount: 7000 },
      { min: 300000, max: 1000000, rate: 0.025, baseAmount: 8500 },
      { min: 1000000, max: 5000000, rate: 0.02, baseAmount: 13500 },
      { min: 5000000, max: null, rate: 0.015, baseAmount: 38500 },
    ],
  },
  bondRegistration: {
    brackets: [
      { min: 0, max: 300000, rate: 0.025, baseAmount: 5000 },
      { min: 300000, max: 1000000, rate: 0.02, baseAmount: 6500 },
      { min: 1000000, max: 5000000, rate: 0.015, baseAmount: 11500 },
      { min: 5000000, max: null, rate: 0.01, baseAmount: 36500 },
    ],
  },
  deedsOffice: {
    minimumFee: 350,
    rate: 0.001,
  },
  disbursements: {
    estimated: 3500,
  },
};

// ─── LSSA Attorney Fee Calculation (Tech Spec Section 4.1.1 / 4.2.1) ───────
// 5 step-based brackets with Math.ceil() stepping
// Returns fee INCLUSIVE of VAT

function calculateLSSAFee(amount: number, vatRate: number): number {
  if (amount <= 0) return 0;

  let fee: number;
  if (amount <= 100000) {
    fee = 6640;
  } else if (amount <= 500000) {
    fee = 6640 + Math.ceil((amount - 100000) / 50000) * 1060;
  } else if (amount <= 1000000) {
    fee = 15120 + Math.ceil((amount - 500000) / 100000) * 2050;
  } else if (amount <= 5000000) {
    fee = 25370 + Math.ceil((amount - 1000000) / 200000) * 2050;
  } else {
    fee = 66370 + Math.ceil((amount - 5000000) / 1000000) * 5160;
  }

  return Math.round(fee * (1 + vatRate));
}

// ─── Transfer Duty (SARS 2025/26) — Tech Spec Section 4.3 ──────────────────
// Progressive sliding scale, NOT subject to VAT

export function calculateTransferDuty(
  price: number,
  _purchaserType: PurchaserType = 'Natural_person'
): number {
  if (price <= 0) return 0;
  // All purchaser types currently use the same sliding scale per SARS regime
  if (price <= 1210000)  return 0;
  if (price <= 1663800)  return (price - 1210000) * 0.03;
  if (price <= 2329300)  return 13614 + (price - 1663800) * 0.06;
  if (price <= 2994800)  return 53544 + (price - 2329300) * 0.08;
  if (price <= 13310000) return 106784 + (price - 2994800) * 0.11;
  return 1241456 + (price - 13310000) * 0.13;
}

// ─── Bond Attorney Fee — Tech Spec Section 4.1.1 ───────────────────────────

export function calculateBondRegFee(
  bondAmount: number,
  vatRate: number = DEFAULT_CALCULATOR_CONFIG.vatRate
): number {
  return calculateLSSAFee(bondAmount, vatRate);
}

// ─── Transfer Attorney Fee — Tech Spec Section 4.2.1 ───────────────────────

export function calculateTransferAttorneyFee(
  purchasePrice: number,
  vatRate: number = DEFAULT_CALCULATOR_CONFIG.vatRate
): number {
  return calculateLSSAFee(purchasePrice, vatRate);
}

// ─── Main Orchestrator — Tech Spec Section 7 ────────────────────────────────

export function calculateTransferCosts(
  purchasePrice: number,
  bondAmount: number,
  purchaserType: PurchaserType = 'Natural_person',
  config: CalculatorConfig = DEFAULT_CALCULATOR_CONFIG
): TransferCostResult {
  // Bond Registration Costs
  const bondAttorneyFee = calculateBondRegFee(bondAmount, config.vatRate);
  const bankInitFee = config.bankInitFee;
  const bondDeedsFee = config.bondDeedsFee;
  const bondPostFica = config.postFicaFee;
  const bondTotal = bondAttorneyFee + bankInitFee + bondDeedsFee + bondPostFica;

  // Property Transfer Costs
  const transferAttorneyFee = calculateTransferAttorneyFee(purchasePrice, config.vatRate);
  const transferDuty = calculateTransferDuty(purchasePrice, purchaserType);
  const transferDeedsFee = config.transferDeedsFee;
  const transferPostFica = config.postFicaFee;
  const transferTotal = transferAttorneyFee + transferDuty + transferDeedsFee + transferPostFica;

  return {
    bondAttorneyFee,
    bankInitFee,
    bondDeedsFee,
    bondPostFica,
    bondTotal,
    transferAttorneyFee,
    transferDuty,
    transferDeedsFee,
    transferPostFica,
    transferTotal,
    grandTotal: bondTotal + transferTotal,
  };
}

// ─── Legacy API (preserved for Affordability, Repayment, etc. tabs) ─────────

/**
 * @deprecated Use calculateTransferDuty() directly — this uses the legacy bracket config
 */
export function calculateTransferFees(
  price: number,
  config: FinancialConfig = DEFAULT_FINANCIAL_CONFIG
): number {
  for (const bracket of config.transferFees.brackets) {
    if (price >= bracket.min && (bracket.max === null || price <= bracket.max)) {
      return price * bracket.rate + bracket.baseAmount;
    }
  }
  return 0;
}

/**
 * @deprecated Use calculateBondRegFee() directly
 */
export function calculateBondRegistrationFees(
  bondAmount: number,
  config: FinancialConfig = DEFAULT_FINANCIAL_CONFIG
): number {
  if (bondAmount <= 0) return 0;
  for (const bracket of config.bondRegistration.brackets) {
    if (bondAmount >= bracket.min && (bracket.max === null || bondAmount <= bracket.max)) {
      return bondAmount * bracket.rate + bracket.baseAmount;
    }
  }
  return 0;
}

/**
 * @deprecated Use config.bondDeedsFee / config.transferDeedsFee directly
 */
export function calculateDeedsOfficeFees(
  price: number,
  config: FinancialConfig = DEFAULT_FINANCIAL_CONFIG
): number {
  return Math.max(config.deedsOffice.minimumFee, price * config.deedsOffice.rate);
}

/**
 * @deprecated Use calculateTransferCosts() for the transfer cost calculator tab
 * This legacy function is kept for the Affordability calculator and other tabs
 */
export function calculateTotalTransferCosts(
  price: number,
  bondAmount: number = 0,
  includeBondCosts: boolean = false,
  config: FinancialConfig = DEFAULT_FINANCIAL_CONFIG
) {
  const transferDuty = calculateTransferDuty(price);
  const transferFees = calculateTransferFees(price, config);
  const deedsOfficeFees = calculateDeedsOfficeFees(price, config);
  const bondRegistrationFees = includeBondCosts ? calculateBondRegistrationFees(bondAmount, config) : 0;
  const disbursements = config.disbursements.estimated;

  const totalCosts = transferDuty + transferFees + bondRegistrationFees + deedsOfficeFees + disbursements;

  return {
    transferDuty,
    transferFees,
    bondRegistrationFees,
    deedsOfficeFees,
    estimatedDisbursements: disbursements,
    totalCosts,
  };
}
